import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, Trophy, MapPin, Store } from 'lucide-react'
import { config } from '@workspace/public-utils'

interface WinningStore {
  store_rank: number
  store_name: string
  number_type: string
  location: string
}

interface LotteryDetailProps {
  roundNumber: number
}

const LotteryResultDetail: React.FC<LotteryDetailProps> = ({ roundNumber }) => {
  const [prizeDetailsExpanded, setPrizeDetailsExpanded] = useState(false)
  const [storesExpanded, setStoresExpanded] = useState(false)
  const [detailData, setDetailData] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Reset detail data when round number changes
    setDetailData(null)
    if (prizeDetailsExpanded || storesExpanded) {
      fetchDetailData()
    }
  }, [prizeDetailsExpanded, storesExpanded, roundNumber])

  const fetchDetailData = async () => {
    // Always fetch for the current round, don't use cached data from different rounds
    setLoading(true)
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/lottery/results/${roundNumber}`
      )
      if (response.ok) {
        const data = await response.json()
        console.log(`Fetched detail data for round ${roundNumber}:`, data) // Debug log
        
        // Parse prize_details if it's a JSON string
        if (data.prize_details && typeof data.prize_details === 'string') {
          try {
            data.prize_details = JSON.parse(data.prize_details)
          } catch (e) {
            console.error('Error parsing prize_details:', e)
            data.prize_details = {}
          }
        }
        
        setDetailData(data)
      } else {
        console.error(`Failed to fetch detail data: ${response.status}`)
      }
    } catch (error) {
      console.error('Error fetching detail data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 100000000) {
      return `${(amount / 100000000).toFixed(1)}억원`
    } else if (amount >= 10000) {
      return `${(amount / 10000).toFixed(0)}만원`
    }
    return `${amount.toLocaleString()}원`
  }

  const prizeDetails = detailData?.prize_details || {}
  const winningStores = detailData?.winning_stores || []
  
  // Check if prize details exist and have data
  const hasPrizeDetails = prizeDetails && Object.keys(prizeDetails).length > 0
  const hasWinningStores = winningStores && winningStores.length > 0

  return (
    <div className="mt-4 space-y-3">
      {/* Prize Details Accordion */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setPrizeDetailsExpanded(!prizeDetailsExpanded)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 hover:from-yellow-100 hover:to-yellow-200 dark:hover:from-yellow-900/30 dark:hover:to-yellow-800/30 transition-all"
        >
          <div className="flex items-center space-x-2">
            <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <span className="font-bold text-gray-900 dark:text-white">당첨 내역 (1등~5등)</span>
          </div>
          {prizeDetailsExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </button>

        <AnimatePresence>
          {prizeDetailsExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              {loading ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-happiness-primary mx-auto"></div>
                  <p className="mt-2">데이터 로딩 중...</p>
                </div>
              ) : hasPrizeDetails ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">순위</th>
                        <th className="px-4 py-3 text-right font-bold text-gray-700 dark:text-gray-300">총 당첨금</th>
                        <th className="px-4 py-3 text-right font-bold text-gray-700 dark:text-gray-300">당첨자수</th>
                        <th className="px-4 py-3 text-right font-bold text-gray-700 dark:text-gray-300">당첨금액</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">조건</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['rank1', 'rank2', 'rank3', 'rank4', 'rank5'].map((rankKey, index) => {
                        const rank = prizeDetails[rankKey]
                        if (!rank) return null

                        return (
                          <tr key={rankKey} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">{index + 1}등</td>
                            <td className="px-4 py-3 text-right text-yellow-600 font-bold">
                              {formatCurrency(rank.total_prize)}
                            </td>
                            <td className="px-4 py-3 text-right dark:text-gray-300">{rank.winners.toLocaleString()}명</td>
                            <td className="px-4 py-3 text-right font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(rank.prize_per_winner)}
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                              {rank.description}
                              {rank.method && (
                                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{rank.method}</div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <p>당첨 내역 정보가 없습니다.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Winning Stores Accordion */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setStoresExpanded(!storesExpanded)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 hover:from-green-100 hover:to-green-200 dark:hover:from-green-900/30 dark:hover:to-green-800/30 transition-all"
        >
          <div className="flex items-center space-x-2">
            <Store className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="font-bold text-gray-900 dark:text-white">1등 배출 판매점</span>
          </div>
          {storesExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </button>

        <AnimatePresence>
          {storesExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              {loading ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-happiness-primary mx-auto"></div>
                  <p className="mt-2">데이터 로딩 중...</p>
                </div>
              ) : hasWinningStores ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-center font-bold text-gray-700 dark:text-gray-300">순위</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">판매점명</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700 dark:text-gray-300">방식</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-300">위치</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winningStores.map((store: WinningStore) => (
                        <tr
                          key={`${store.store_rank}-${store.store_name}`}
                          className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="px-4 py-3 text-center font-bold text-gray-900 dark:text-white">
                            {store.store_rank}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                            {store.store_name}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-bold ${
                                store.number_type === '자동'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-purple-100 text-purple-700'
                              }`}
                            >
                              {store.number_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            <div className="flex items-start space-x-1">
                              <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm">{store.location}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <p>1등 배출 판매점 정보가 없습니다.</p>
                  <p className="text-xs mt-1">데이터가 수집되지 않았거나 아직 확인되지 않은 회차입니다.</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default LotteryResultDetail

